import {
  Button,
  Modal,
  OutlinedSelectOptionType,
  Select,
  addToast,
} from '@octano/global-ui';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

export interface CohortRangeModalProps {
  isOpen: boolean;
  title: string;
  subtitle?: string;
  /** Mensaje de advertencia destacado (p. ej. irreversibilidad en el alta inicial). */
  warning?: string;
  /** Año "Desde" preseleccionado (modo edición). */
  defaultFromYear?: number;
  /** Año "Hasta" preseleccionado (modo edición). */
  defaultToYear?: number;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (fromYear: number, toYear: number) => void;
}

const FROM_YEAR = 2000;

/**
 * Modal reutilizable para agregar un rango de cohorte compartido. Se usa tanto
 * para el rango inicial (al activar el check) como para el botón global
 * "+ AGREGAR RANGO". Expone dos dropdowns de año (Desde / Hasta).
 */
export default function CohortRangeModal({
  isOpen,
  title,
  subtitle,
  warning,
  defaultFromYear,
  defaultToYear,
  loading,
  onClose,
  onConfirm,
}: CohortRangeModalProps) {
  const { t } = useTranslation();
  const prefix = 'tuitionContinuityTariff.cohort';

  const [fromYear, setFromYear] = useState<number | undefined>(undefined);
  const [toYear, setToYear] = useState<number | undefined>(undefined);

  // Al abrir, preselecciona los años recibidos (modo edición) o limpia (alta).
  useEffect(() => {
    if (isOpen) {
      setFromYear(defaultFromYear);
      setToYear(defaultToYear);
    }
  }, [isOpen, defaultFromYear, defaultToYear]);

  // Lista de años generada en orden descendente (año actual + 1 ... 2000).
  const yearOptions = useMemo<OutlinedSelectOptionType[]>(() => {
    const currentYear = new Date().getFullYear();
    const lastYear = currentYear + 1;
    const options: OutlinedSelectOptionType[] = [];
    for (let year = lastYear; year >= FROM_YEAR; year -= 1) {
      options.push({ label: String(year), value: year });
    }
    return options;
  }, []);

  const handleConfirm = () => {
    if (fromYear === undefined || toYear === undefined) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t(`${prefix}.rangeOverlapError`),
      });
      return;
    }
    if (fromYear > toYear) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t(`${prefix}.rangeOverlapError`),
      });
      return;
    }
    onConfirm(fromYear, toYear);
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="md" unmountOnClose>
      <h2 className="text-primary fs-18 fw-600 text-uppercase mb-2">{title}</h2>
      {subtitle && <p className="mb-4">{subtitle}</p>}
      {warning && (
        <p className="text-danger fw-600 mb-4" role="alert">
          {warning}
        </p>
      )}
      <Row className="align-items-start">
        <Col xs={6}>
          <Select
            name="cohort-range-from"
            label={t(`${prefix}.rangeFrom`)}
            isClearable={false}
            options={yearOptions}
            value={yearOptions.find((option) => option.value === fromYear)}
            onChange={(option: OutlinedSelectOptionType) =>
              setFromYear(Number(option.value))
            }
          />
        </Col>
        <Col xs={6}>
          <Select
            name="cohort-range-to"
            label={t(`${prefix}.rangeTo`)}
            isClearable={false}
            options={yearOptions}
            value={yearOptions.find((option) => option.value === toYear)}
            onChange={(option: OutlinedSelectOptionType) =>
              setToYear(Number(option.value))
            }
          />
        </Col>
      </Row>
      <div
        className="d-flex justify-content-between mt-4"
        style={{ gap: '1.25rem' }}
      >
        <Button
          type="button"
          text={t('common.actions.cancel')}
          className="mt-0"
          outlined
          fullwidth
          onClick={onClose}
        />
        <Button
          type="button"
          text={t('common.actions.confirm')}
          className="mt-0"
          fullwidth
          loading={loading}
          onClick={handleConfirm}
        />
      </div>
    </Modal>
  );
}
